import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  SectionWrapper,
  SectionTitle,
  SpanTwo,
  VehicleDataGrid,
  CheckboxWrapper,
} from "./SharedSectionStyles";
import {
  objectGroupOptions,
  bodyTypeOptions,
  fuelTypeOptions,
  getTerritoryOfValidityOptions,
} from "./optionLists";
import CompactField from "components/CompactField/CompactField";
import { useTaxonomy } from "library/utils/useTaxonomy";
import { useTaxonomyFieldConverter } from "library/utils/useTaxonomyValue";
import { ValidationErrorDisplay } from "components/ValidationErrorDisplay";
import { useDriverDataSync } from "./useDriverDataSync";

interface VehicleDataSectionProps {
  isCasco?: boolean;
  getError?: (fieldPath: string) => string | undefined;
  isPrefilling?: boolean;
  offerId?: string | null;
}

const VehicleDataSection: React.FC<VehicleDataSectionProps> = ({
  isCasco = false,
  getError,
  isPrefilling = false,
  offerId,
}) => {
  const { t } = useTranslation();
  const taxonomy = useTaxonomy();
  const { watch, setValue } = useFormContext();
  const syncDriverData = useDriverDataSync();

  // Watch the isLeased checkbox to conditionally require owner email and phone
  const isLeased = watch("vehicleData.isLeased");

  // Auto-convert IDs to codes for taxonomy fields
  useTaxonomyFieldConverter("vehicleData.objectGroup", "VehicleLegalType");
  useTaxonomyFieldConverter("vehicleData.objectGroupType", "VehicleType");
  useTaxonomyFieldConverter(
    "vehicleData.vehicleBodyTypeCode",
    "VehicleBodyType"
  );
  useTaxonomyFieldConverter(
    "vehicleData.vehiclePowerTypeCode",
    "VehiclePowerType"
  );
  useTaxonomyFieldConverter("vehicleData.objectUsage", "VehicleUsageTypeLV");
  useTaxonomyFieldConverter(
    "vehicleData.vehicleTransportTypeCode",
    "VehicleTransportType"
  );
  useTaxonomyFieldConverter("clientData.territoryOfValidity", "CoverageTerritory");

  // Watch the selected values for dynamic filtering
  const selectedObjectGroup = watch("vehicleData.objectGroup");
  const selectedObjectGroupType = watch("vehicleData.objectGroupType");

  // Create options for Vehicle Power Type (VehiclePowerType)
  const vehiclePowerTypeCodeOptionsWithTaxonomy = useMemo(() => {
    if (!taxonomy?.VehiclePowerType?.items) {
      return fuelTypeOptions; // Fallback to hardcoded options
    }

    return taxonomy.VehiclePowerType.items.map((item) => ({
      key: item.code,
      text: t(`${item.code}`, item.code),
    }));
  }, [taxonomy, t]);

  // Create options for Vehicle Body Type (VehicleBodyType)
  const vehicleBodyTypeCodeOptionsWithTaxonomy = useMemo(() => {
    if (!taxonomy?.VehicleBodyType?.items) {
      return bodyTypeOptions; // Fallback to hardcoded options
    }

    return taxonomy.VehicleBodyType.items.map((item) => ({
      key: item.code,
      text: t(`${item.code}`, item.code),
    }));
  }, [taxonomy, t]);

  // Create options for Vehicle Usage Type (VehicleUsageTypeLV)
  const vehicleUsageTypeOptionsWithTaxonomy = useMemo(() => {
    if (!taxonomy?.VehicleUsageTypeLV?.items) {
      return []; // No fallback, this is taxonomy-only
    }

    // Return all items, display Latvian code text directly
    return taxonomy.VehicleUsageTypeLV.items.map((item) => ({
      key: item.code, // Store code in the form (will be converted to ID when sending to API)
      text: item.code, // Display Latvian text directly without translation
    }));
  }, [taxonomy]);

  // Create options for Vehicle Transport Type (VehicleTransportType)
  // Use code as display text without translations (these codes don't have translations)
  const vehicleTransportTypeCodeOptionsWithTaxonomy = useMemo(() => {
    if (!taxonomy?.VehicleTransportType?.items) {
      return []; // No fallback, this is taxonomy-only
    }

    return taxonomy.VehicleTransportType.items.map((item) => ({
      key: item.code,
      text: item.code, // Use code directly without translation - no translations exist for these
    }));
  }, [taxonomy]);

  // Create options for Object Group (VehicleLegalType)
  // Only include VehicleLegalTypes that have VehicleType children
  const objectGroupOptionsWithTaxonomy = useMemo(() => {
    if (!taxonomy?.VehicleLegalType?.items || !taxonomy?.VehicleType?.items) {
      return objectGroupOptions; // Fallback to hardcoded options
    }

    // Find all VehicleLegalType IDs that have VehicleType children
    const parentIdsWithChildren = new Set(
      taxonomy.VehicleType.items
        .filter((item) => item.parentId)
        .map((item) => item.parentId)
    );

    // Only return VehicleLegalTypes that have children
    return taxonomy.VehicleLegalType.items
      .filter((item) => parentIdsWithChildren.has(item.id))
      .map((item) => ({
        key: item.code,
        text: t(`${item.code}`, item.code),
      }));
  }, [taxonomy, t]);

  // Create hierarchical options for Object Group Type (VehicleType)
  // Grouped by parent VehicleLegalType using OptionGroup
  // Filtered by selected Object Group if one is selected
  // VehicleType codes don't have translations - use codes directly
  const objectGroupTypeOptions = useMemo(() => {
    if (!taxonomy?.VehicleType?.items || !taxonomy?.VehicleLegalType?.byId) {
      return objectGroupOptions; // Fallback to hardcoded options
    }

    // Group VehicleType items by their parent VehicleLegalType
    const grouped: {
      [parentId: number]: {
        parent: any;
        children: Array<{ key: string; text: string }>;
      };
    } = {};

    taxonomy.VehicleType.items.forEach((vehicleType) => {
      if (vehicleType.parentId) {
        const parent = taxonomy.VehicleLegalType.byId[vehicleType.parentId];
        if (parent) {
          // If an Object Group is selected, only include children of that group
          if (selectedObjectGroup && parent.code !== selectedObjectGroup) {
            return;
          }

          if (!grouped[vehicleType.parentId]) {
            grouped[vehicleType.parentId] = {
              parent,
              children: [],
            };
          }
          const group = grouped[vehicleType.parentId];
          if (group) {
            group.children.push({
              key: vehicleType.code,
              text: vehicleType.code, // Use code directly without translation
            });
          }
        }
      }
    });

    // Build options array with children for OptionGroup
    const options: Array<{
      key: string;
      text: string;
      children: Array<{ key: string; text: string }>;
    }> = [];
    Object.values(grouped).forEach(({ parent, children }) => {
      options.push({
        key: parent.code,
        text: t(`${parent.code}`, parent.code) as string,
        children: children,
      });
    });

    return options;
  }, [taxonomy, t, selectedObjectGroup]);

  // Create options for Coverage Territory (CoverageTerritory) - used in CASCO row 4
  const territoryOfValidityOptions = useMemo(() => {
    if (!taxonomy?.CoverageTerritory?.items) {
      return getTerritoryOfValidityOptions(t);
    }
    return taxonomy.CoverageTerritory.items.map((item: any) => ({
      key: item.code,
      text: t(item.code, item.code.split(".")[1]?.replace(/_/g, " ") || item.code),
    }));
  }, [taxonomy, t]);

  // Clear CASCO-only fields when switching from CASCO to MTPL
  const prevIsCascoRef = React.useRef<boolean>(isCasco);
  React.useEffect(() => {
    if (prevIsCascoRef.current && !isCasco) {
      setValue("vehicleData.vehicleValue", "");
      setValue("clientData.territoryOfValidity", "");
    }
    prevIsCascoRef.current = isCasco;
  }, [isCasco, setValue]);

  // Use ref to track if we're programmatically updating to avoid circular dependencies
  const isUpdatingRef = React.useRef(false);

  // Auto-select Object Group when Object Group Type changes
  React.useEffect(() => {
    if (isUpdatingRef.current) return;

    if (
      selectedObjectGroupType &&
      taxonomy?.VehicleType?.byCode &&
      taxonomy?.VehicleLegalType?.byId
    ) {
      const vehicleType = taxonomy.VehicleType.byCode[selectedObjectGroupType];
      if (vehicleType && vehicleType.parentId) {
        const parent = taxonomy.VehicleLegalType.byId[vehicleType.parentId];
        if (parent && parent.code !== selectedObjectGroup) {
          isUpdatingRef.current = true;
          setValue("vehicleData.objectGroup", parent.code);
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 0);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObjectGroupType, taxonomy, setValue]);

  // Clear Object Group Type selection when Object Group changes
  React.useEffect(() => {
    if (isUpdatingRef.current) return;

    if (
      selectedObjectGroup &&
      selectedObjectGroupType &&
      taxonomy?.VehicleType?.byCode
    ) {
      const vehicleType = taxonomy.VehicleType.byCode[selectedObjectGroupType];
      if (
        vehicleType &&
        vehicleType.parentId &&
        taxonomy?.VehicleLegalType?.byId
      ) {
        const parent = taxonomy.VehicleLegalType.byId[vehicleType.parentId];
        // If the current Object Group Type doesn't belong to the selected Object Group, clear it
        if (parent && parent.code !== selectedObjectGroup) {
          isUpdatingRef.current = true;
          setValue("vehicleData.objectGroupType", "");
          setTimeout(() => {
            isUpdatingRef.current = false;
          }, 0);
        }
      }
    }
  }, [selectedObjectGroup, selectedObjectGroupType, taxonomy, setValue]);

  // Get current year for default value
  const currentYear = new Date().getFullYear();

  // Determine if fields should be read-only (when offerId exists)
  const isReadOnly = !!offerId;

  return (
    <SectionWrapper>
      <SectionTitle>{t("qtg.proposal.generalInfo.vehicleData")}</SectionTitle>

      <VehicleDataGrid>
        {/* Row 1: Basic Vehicle Info */}
        <div>
          <CompactField
            type="input"
            name="vehicleData.vehicleMake"
            dataHelp="offer-general-vehicle-make"
            label="Vehicle Make"
            translationKey="qtg.proposal.vehicleData.vehicleMake"
            placeholder=""
            required={true}
            readOnly={isReadOnly}
          />
          {getError && (
            <ValidationErrorDisplay error={getError("Vehicle.Brand")} />
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "10fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div>
            <CompactField
              type="input"
              name="vehicleData.vehicleModel"
              dataHelp="offer-general-vehicle-model"
              label="Vehicle Model"
              translationKey="qtg.proposal.vehicleData.vehicleModel"
              placeholder=""
              required={true}
              readOnly={isReadOnly}
            />
            {getError && (
              <ValidationErrorDisplay error={getError("Vehicle.Model")} />
            )}
          </div>
          <CheckboxWrapper>
            <CompactField
              type="checkbox"
              name="vehicleData.convertible"
              label="Convertible"
              translationKey="qtg.proposal.vehicleData.convertible"
              readOnly={isReadOnly}
            />
          </CheckboxWrapper>
        </div>
        <CompactField
          type="dropdown"
          name="vehicleData.objectGroup"
          dataHelp="offer-general-object-group"
          label="Object Group"
          translationKey="qtg.proposal.vehicleData.objectGroup"
          options={objectGroupOptionsWithTaxonomy}
          placeholder=""
          readOnly={isReadOnly}
        />{" "}
        <CompactField
          type="dropdown"
          name="vehicleData.objectGroupType"
          dataHelp="offer-general-object-group-type"
          label="Object Group Type"
          translationKey="qtg.proposal.vehicleData.objectGroupType"
          options={objectGroupTypeOptions}
          placeholder=""
          readOnly={isReadOnly}
        />
        {/* Row 2: Technical Details */}
        <SpanTwo>
          <CompactField
            type="dropdown"
            name="vehicleData.vehicleTransportTypeCode"
            dataHelp="offer-general-transport-type"
            label="Vehicle Transport Type"
            translationKey="qtg.proposal.vehicleData.vehicleTransportTypeCode.label"
            options={vehicleTransportTypeCodeOptionsWithTaxonomy}
            placeholder=""
            readOnly={isReadOnly}
          />
        </SpanTwo>
        <CompactField
          type="dropdown"
          name="vehicleData.vehiclePowerTypeCode"
          dataHelp="offer-general-fuel-type"
          label="Fuel Type"
          translationKey="qtg.proposal.vehicleData.vehiclePowerTypeCode"
          options={vehiclePowerTypeCodeOptionsWithTaxonomy}
          placeholder=""
          readOnly={isReadOnly}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div>
            <CompactField
              type="inputWithUnit"
              name="vehicleData.engineCapacity"
              dataHelp="offer-general-engine-capacity"
              label="Engine Capacity"
              translationKey="qtg.proposal.vehicleData.engineCapacity"
              placeholder=""
              unit="cm³"
              inputStyle={{ width: "120px" }}
              required={true}
              readOnly={isReadOnly}
            />
            {getError && (
              <ValidationErrorDisplay
                error={getError("Vehicle.CubicCapacityCcm")}
              />
            )}
          </div>
          <div>
            <CompactField
              type="inputWithUnit"
              name="vehicleData.power"
              dataHelp="offer-general-power"
              label="Power"
              translationKey="qtg.proposal.vehicleData.power"
              placeholder=""
              unit="kW"
              inputStyle={{ width: "30px" }}
              required={true}
              readOnly={isReadOnly}
            />
            {getError && (
              <ValidationErrorDisplay error={getError("Vehicle.PowerKw")} />
            )}
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "10fr 1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div>
            <CompactField
              type="input"
              name="vehicleData.productionYear"
              dataHelp="offer-general-production-year"
              label="Production Year"
              translationKey="qtg.proposal.vehicleData.productionYear"
              inputType="number"
              placeholder={currentYear.toString()}
              required={true}
              inputStyle={{ width: "70px" }}
              readOnly={isReadOnly}
            />
            {getError && (
              <ValidationErrorDisplay
                error={getError("Vehicle.ManufactureDate")}
              />
            )}
          </div>
          <div style={{ alignSelf: "end", transform: "translateY(4px)" }}>
            <CompactField
              type="checkbox"
              name="vehicleData.isNew"
              dataHelp="offer-general-is-new"
              label="New"
              translationKey="qtg.proposal.vehicleData.isNew"
              readOnly={isReadOnly}
            />
          </div>
          <div style={{ alignSelf: "end", transform: "translateY(4px)" }}>
            <CompactField
              type="checkbox"
              name="vehicleData.keyless"
              dataHelp="offer-general-keyless"
              label="Keyless"
              translationKey="qtg.proposal.vehicleData.keyless"
              readOnly={isReadOnly}
            />
          </div>
        </div>
        <CompactField
          type="dropdown"
          name="vehicleData.vehicleBodyTypeCode"
          dataHelp="offer-general-body-type"
          label="Body Type"
          translationKey="qtg.proposal.vehicleData.vehicleBodyTypeCode"
          options={vehicleBodyTypeCodeOptionsWithTaxonomy}
          placeholder=""
          readOnly={isReadOnly}
        />
        <CompactField
          type="dropdown"
          name="vehicleData.objectUsage"
          dataHelp="offer-general-usage-type"
          label="Vehicle Usage Type"
          translationKey="qtg.proposal.vehicleData.objectUsage.label"
          options={vehicleUsageTypeOptionsWithTaxonomy}
          placeholder=""
          readOnly={isReadOnly}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div>
            <CompactField
              type="inputWithUnit"
              name="vehicleData.maximumGrossWeight"
              dataHelp="offer-general-gross-weight"
              label="Maximum Gross Weight"
              translationKey="qtg.proposal.vehicleData.maximumGrossWeight"
              placeholder=""
              unit="kg"
              inputStyle={{ width: "130px" }}
              required={true}
              readOnly={isReadOnly}
            />
            {getError && (
              <ValidationErrorDisplay
                error={getError("Vehicle.MaximumGrossWeight")}
              />
            )}
          </div>
          <CompactField
            type="input"
            name="vehicleData.seats"
            label="Seats"
            translationKey="qtg.proposal.vehicleData.seats"
            inputType="number"
            placeholder=""
            style={{ width: "60px" }}
            readOnly={isReadOnly}
          />
        </div>
        {/* Row 4: CASCO-specific fields */}
        {isCasco && (
          <>
            <CompactField
              type="inputWithUnit"
              name="vehicleData.vehicleValue"
              dataHelp="offer-general-vehicle-value"
              label="Vehicle Value"
              translationKey="qtg.proposal.vehicleData.vehicleValue"
              placeholder=""
              inputStyle={{ width: "70px" }}
              unit="EUR"
              inputType="number"
              required={true}
              readOnly={isReadOnly}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px 16px",
                alignItems: "end",
              }}
            >
              <div>
                <CompactField
                  type="inputWithUnit"
                  name="vehicleData.otherDriverAge"
                  dataHelp="offer-general-other-driver-age"
                  label="Other Driver Age"
                  translationKey="qtg.proposal.vehicleData.otherDriverAge"
                  inputType="number"
                  unit="years"
                  inputStyle={{ width: "80px" }}
                  required={true}
                  readOnly={isReadOnly}
                  onBlur={() => syncDriverData("vehicleData.otherDriverAge")}
                />
                {getError && (
                  <ValidationErrorDisplay
                    error={getError("Vehicle.OtherDriverAge")}
                  />
                )}
              </div>
              <div>
                <CompactField
                  type="inputWithUnit"
                  name="vehicleData.otherDriverExperience"
                  dataHelp="offer-general-other-driver-experience"
                  label="Other Driver Experience"
                  translationKey="qtg.proposal.vehicleData.otherDriverExperience"
                  inputType="number"
                  unit="years"
                  inputStyle={{ width: "80px" }}
                  required={true}
                  readOnly={isReadOnly}
                  onBlur={() =>
                    syncDriverData("vehicleData.otherDriverExperience")
                  }
                />
                {getError && (
                  <ValidationErrorDisplay
                    error={getError("Vehicle.OtherDriverExperience")}
                  />
                )}
              </div>
            </div>
            <div>
              <CompactField
                type="dropdown"
                name="clientData.territoryOfValidity"
                dataHelp="offer-general-coverage-territory"
                label={t("qtg.proposal.fields.coverageTerritory", "Coverage territory")}
                translationKey="qtg.proposal.clientData.territoryOfValidity"
                options={territoryOfValidityOptions}
                defaultValue="CoverageTerritory.EUROPE_WITHOUT_CIS"
                required={true}
                readOnly={isReadOnly}
              />
              {getError && (
                <ValidationErrorDisplay
                  error={getError("Insurance.CoverageTerritory")}
                />
              )}
            </div>
            <div></div>
          </>
        )}
        <CompactField
          name="vehicleData.ownerName"
          dataHelp="offer-general-owner-name"
          label={t("qtg.proposal.vehicleData.ownerName.label", "Owner Name")}
          type="input"
          required={true}
          readOnly={isReadOnly}
        />
        {getError && (
          <ValidationErrorDisplay error={getError("Vehicle.Owner.Name")} />
        )}
        {/* Owner Email and Owner Phone fields in a single column */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px 16px",
            alignItems: "end",
          }}
        >
          <div>
            <CompactField
              type="input"
              name="vehicleData.ownerEmail"
              dataHelp="offer-general-owner-email"
              label="Owner e-mail"
              translationKey="qtg.proposal.vehicleData.ownerEmail"
              inputStyle={{ width: "130px" }}
              inputType="email"
              required={isLeased}
              readOnly={isReadOnly}
            />
            {getError && (
              <ValidationErrorDisplay error={getError("Vehicle.Owner.Email")} />
            )}
          </div>
          <div>
            <CompactField
              type="input"
              name="vehicleData.ownerPhone"
              dataHelp="offer-general-owner-phone"
              label="Owner phone number"
              translationKey="qtg.proposal.vehicleData.ownerPhone"
              inputStyle={{ width: "130px" }}
              inputType="tel"
              required={isLeased}
              readOnly={isReadOnly}
            />
            {getError && (
              <ValidationErrorDisplay error={getError("Vehicle.Owner.Phone")} />
            )}
          </div>
        </div>
        {/* Other Driver Information - for CASCO */}
      </VehicleDataGrid>
    </SectionWrapper>
  );
};

export default VehicleDataSection;
